import "../index.css";
import "swiper/css";
import "swiper/css/pagination";

import { Swiper, SwiperSlide } from "swiper/react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import IconBlog from "@assets/iconUpdate/blogSvg.svg";
import IconInstagram from "@assets/iconUpdate/igSvg.svg";
import IconTiktok from "@assets/iconUpdate/ttSvg.svg";
import IconYoutube from "@assets/iconUpdate/ytSvg.svg";
import { useFetch } from "@/Hooks/useFetch";
export const BASE_URL = import.meta.env.VITE_API_URL;
import { NMapRefactored as NMap } from "./Map";

import { Share2Icon, X } from "lucide-react";
import { useSwipeable } from "react-swipeable";
import type { ModelCampaignResponse } from "@models/ModelCampaignResponse";
import type { BaseResponse } from "@models/base";
const S3_URL = import.meta.env.VITE_S3_URL;
import DetailPageSkeleton from "./DetailPageSkeleton";
import { useThemeClass } from "@/Hooks/useThemeClass";
import clsx from "clsx";
import useCampaignHidden from "@/Hooks/useCampaignHidden.ts";
import ListReview from "./ListReview";

type ImageViewerProps = {
  images: string[];
  initialIndex: number;
  onClose: () => void;
};

export const ImageViewer = ({
  images,
  initialIndex,
  onClose,
}: ImageViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const swiperRef = useRef<any>(null);

  const goToImage = (index: number) => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(index);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50 cursor-pointer"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-5 right-5 text-white text-3xl z-50"
        aria-label="Close Image Popup"
      >
        <X />
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full px-5 max-w-4xl h-[80vh] flex justify-center items-center"
      >
        <Swiper
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          initialSlide={initialIndex}
          onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
          spaceBetween={10}
          className="w-full h-full"
        >
          {images.map((img, idx) => (
            <SwiperSlide key={idx}>
              <img
                src={`${S3_URL}${img}`}
                alt={`Slide ${idx}`}
                className="object-contain h-full mx-auto "
              />
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="absolute bottom-[10%] flex space-x-2 justify-center w-full">
          {images.map((_, index) => (
            <div
              key={index}
              className={`h-1 w-6 rounded-md rounded cursor-pointer transition-all duration-300 ${
                index === currentIndex
                  ? "bg-white scale-110"
                  : "bg-gray-400 opacity-50"
              }`}
              onClick={() => goToImage(index)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

function DetailPage() {
  const navigate = useNavigate();
  const themeClass = useThemeClass();
  const { id } = useParams();
  const { fetchData: response, loading } = useFetch<
    BaseResponse<ModelCampaignResponse>
  >({
    url: `${BASE_URL}/user/campaign/`,
    body: {
      id: id,
    },
  });
  const [center, setCenter] = useState({
    latitude: response?.data.latitude,
    longitude: response?.data.longitude,
  });

  const isHidden = useCampaignHidden(response?.data?.announcement_reviewer);

  useEffect(() => {
    if (response?.data?.latitude && response?.data?.longitude) {
      if (response?.data?.latitude > 0 && response?.data?.longitude > 0) {
        setCenter({
          latitude: response?.data?.latitude,
          longitude: response?.data?.longitude,
        });
      }
    }
  }, [response?.data?.latitude, response?.data?.longitude]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  const [isCopied, setIsCopied] = useState(false);
  const handleCopyKeywords = async () => {
    try {
      const keywordsToCopy =
        response?.data?.type === "instagram" &&
        Array.isArray(response?.data?.keyword_list)
          ? response?.data?.keyword_list.join(", ")
          : "";

      await navigator.clipboard.writeText(keywordsToCopy);
      setIsCopied(true);

      setTimeout(() => setIsCopied(false), 3000);
    } catch (err) {
      console.error("복사 실패", err);
    }
  };

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  const openImagePopup = (index: number) => {
    if (response?.data?.images?.[index]) {
      setSelectedImage(response?.data?.images[index]);
      setCurrentIndex(index);
    }
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  const goToImage = (index: number) => {
    if (response?.data?.images) {
      setCurrentIndex(index);
      setSelectedImage(response?.data?.images[index]);
    }
  };

  const handlers = useSwipeable({
    onSwipedLeft: () =>
      response?.data?.images &&
      goToImage((currentIndex + 1) % response?.data?.images.length),
    onSwipedRight: () =>
      response?.data?.images &&
      goToImage(
        (currentIndex - 1 + (response?.data?.images.length || 0)) %
          (response?.data?.images.length || 1)
      ),
    trackMouse: true,
  });

  if (loading) {
    return <DetailPageSkeleton />;
  }

  if (!response?.data) {
    return (
      <div className="flex justify-center items-center h-screen">
        캠페인을 찾을 수 없습니다.
      </div>
    );
  }

  const handleShare = () => {
    const url = `${window.location.origin}/detailPage/${id}`;

    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  return (
    <div className="px-5 mt-6 min-w-full">
      <div className="flex  w-full">
        <button onClick={() => navigate(-1)} className="w-1/12">
          <div className="w-8 h-8">
            <svg
              className="w-full h-full text-gray-800"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1"
                d="m14 8-4 4 4 4"
              />
            </svg>
          </div>
        </button>

        <h1 className="w-9/12 flex font-extrabold text-sm text-center items-center justify-center ps-4 md:ps-10 lg:ps-28">
          {response?.data?.title}
        </h1>
        <div className="w-2/12  justify-end flex gap-2 items-center">
          <div className="relative inline-block">
            <button className="flex justify-items-center" onClick={handleShare}>
              <Share2Icon className="w-5 h-5" />
            </button>

            {copied && (
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-gray-400 text-white text-xs px-2 py-1 rounded whitespace-nowrap shadow z-[999]">
                복사됨
              </span>
            )}
          </div>

          {response?.data?.type === "tiktok" && (
            <img src={IconTiktok} alt="campaign" className="" />
          )}
          {response?.data?.type === "blog" && (
            <img src={IconBlog} alt="campaign" className="" />
          )}
          {response?.data?.type === "instagram" && (
            <img src={IconInstagram} alt="campaign" className="" />
          )}
          {response?.data?.type === "youtube" && (
            <img src={IconYoutube} alt="campaign" className="" />
          )}
        </div>
      </div>
      <div className="flex flex-col text-xs items-center pt-2">
        <div
          className="text-gray-500 "
          dangerouslySetInnerHTML={{ __html: response?.data?.reward ?? "" }}
        ></div>
      </div>
      {/* Important Date */}
      <div className="flex flex-col gap-3 mt-6">
        {/* <h1 className="font-extrabold text-sm">체험일정</h1> */}
        <div
          className={clsx(
            themeClass.bg("background"),
            "p-5 rounded-lg flex flex-col gap-1 text-xs"
          )}
        >
          <div className="flex font-bold">
            <p className="w-3/12 ">신청기간</p>
            <p className="w-9/12">
              {response?.data?.registration_start_date} ~
              {response?.data?.registration_end_date}
            </p>
          </div>
          <div className="flex">
            <p className="w-3/12 text-gray-400">발표 날짜</p>
            <p className="w-9/12 text-gray-400">
              {response?.data?.announcement_reviewer}{" "}
            </p>
          </div>
          <div className="flex">
            <p className="w-3/12 text-gray-400">리뷰 기간</p>
            <p className="w-9/12 text-gray-400">
              {response?.data?.review_start_date} ~{" "}
              {response?.data?.review_end_date}
            </p>
          </div>
          <div className="flex">
            <p className="w-3/12 text-gray-400">신청 인원</p>
            <p className="w-9/12 ">
              <span className="text-red-600 font-semibold">
                {response?.data?.total_apply}
              </span>
              /{response?.data?.max_apply}명
            </p>
          </div>
        </div>
        {/* Image */}
        <div className="overflow-x-auto flex h-[180px] gap-2 w-full mt-2 scrollbar-hide scroll-smooth">
          <div className="flex gap-2 min-w-max">
            {(response.data.images ?? []).map((imagePath, index) => (
              <div
                key={index}
                className="w-40 h-40 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer"
                onClick={() => openImagePopup(index)}
              >
                <img
                  src={`${S3_URL}${imagePath}`}
                  className="w-full h-full object-cover"
                  alt={`Campaign Image ${index + 1}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Information */}
      <div className="flex flex-col gap-3 mt-3 border-b pb-7">
        <h1 className="font-extrabold text-sm">제공내역</h1>
        <div
          className="text-xs text-gray-800 "
          dangerouslySetInnerHTML={{ __html: response?.data?.reward ?? "" }}
        ></div>
      </div>
      {/* Keyword */}
      <div className="flex flex-col gap-3 mt-7 border-b pb-7">
        <div className="flex gap-2">
          <h1 className="font-extrabold text-sm">키워드</h1>
          {isCopied ? (
            <button className="border flex items-center py-0.5 px-2 rounded-full border-gray-400 gap-1 relative">
              <svg
                className="w-4 h-4 text-gray-500 "
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M8.5 11.5 11 14l4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                />
              </svg>

              <span className="text-xs text-gray-400">복사완료</span>
            </button>
          ) : (
            <button
              onClick={handleCopyKeywords}
              className="border flex items-center py-0.5 px-2 rounded-full border-black gap-1 relative"
            >
              <svg
                className="w-4 h-4 text-gray-800"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M9 8v3a1 1 0 0 1-1 1H5m11 4h2a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-7a1 1 0 0 0-1 1v1m4 3v10a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-7.13a1 1 0 0 1 .24-.65L7.7 8.35A1 1 0 0 1 8.46 8H13a1 1 0 0 1 1 1Z"
                />
              </svg>
              <span className="text-xs">복사하기</span>
            </button>
          )}
        </div>

        <div className="flex gap-1 text-xs text-center flex-wrap">
          {response?.data?.type === "instagram" ? (
            <>
              {response?.data?.keyword_list?.map((word, index) => (
                <div
                  key={index}
                  className={clsx(
                    themeClass.bg("background"),
                    "py-1 rounded-full border px-3"
                  )}
                >
                  {word}
                </div>
              ))}
            </>
          ) : (
            <>
              {response?.data?.keyword_list?.map((word, index) => (
                <div
                  key={index}
                  className={clsx(
                    themeClass.bg("background"),
                    "py-1 rounded-full border px-3"
                  )}
                >
                  {word}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3 mt-7 border-b pb-7">
        <h1 className="font-extrabold text-sm">블로거 방문가능시간</h1>
        <div
          className="text-xs text-gray-800 "
          dangerouslySetInnerHTML={{ __html: response?.data?.info ?? "" }}
        ></div>
      </div>
      <div className="flex flex-col gap-2 mt-7 border-b pb-7">
        <div className="flex gap-2 flex-wrap">
          <h1 className="font-extrabold text-sm">플레이스</h1>
          <Link to={response?.data?.link ?? ""}>
            <button className="border flex items-center py-0.5 px-2 rounded-full border-black gap-1 relative">
              <svg
                className="w-4 h-4 text-gray-800"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                />
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17.8 13.938h-.011a7 7 0 1 0-11.464.144h-.016l.14.171c.1.127.2.251.3.371L12 21l5.13-6.248c.194-.209.374-.429.54-.659l.13-.155Z"
                />
              </svg>
              <span className="text-xs">지도링크번</span>
            </button>
          </Link>
        </div>
        {/* Map */}
        <NMap
          center={center}
          zoom={16}
          className="w-full h-[300px] border border-slate-300 rounded mt-2"
          currentCampaign={response?.data}
        />
      </div>
      {/* Information */}
      {/* <div className="flex flex-col gap-3 mt-6 border-b pb-7">
        <h1 className="font-extrabold text-sm">업체예약정보</h1>
        <div
          className="text-xs text-gray-800 "
          dangerouslySetInnerHTML={{ __html: dataCampaign?.info }}
        ></div>
      </div> */}
      <div className="flex flex-col gap-3 mt-7 border-b pb-7">
        <h1 className="font-extrabold text-sm">가이드라인</h1>

        {Array.isArray(response?.data?.icon_review_list) &&
          response?.data?.icon_review_list.length > 0 && (
            <div className="overflow-x-auto">
              <div className="flex gap-4 mt-2 pb-2 w-max">
                {response.data.icon_review_list.map((icon, index) => (
                  <div key={index} className="flex flex-col items-center">
                    <img
                      src={`${S3_URL}${icon.path}`}
                      className="w-12 h-12"
                      alt={icon.name}
                    />
                    <p className="text-[10px] pt-1 text-center">{icon.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Guidelines */}
        <div
          className="text-xs text-gray-500 "
          dangerouslySetInnerHTML={{ __html: response?.data?.guidelines ?? "" }}
        ></div>
      </div>
      <div className="flex flex-col gap-3 mt-7 ">
        <h1 className="font-extrabold text-sm">주의사항</h1>
        <div
          className="text-xs text-gray-500"
          dangerouslySetInnerHTML={{ __html: response?.data?.note ?? "" }}
        ></div>
      </div>
      {/*<ListReview data={response?.data?.review_list} />*/}
      {!response?.data?.is_famtour && (
        <Link to={isHidden ? "#" : `/formdetailPage/${response?.data?.id}`}>
          <div className="flex flex-col gap-3 mt-9 text-sm">
            <button
              className={`py-3 rounded-full text-white ${themeClass.bgNohex(
                "text2"
              )} ${isHidden ? "bg-[#384d928e] cursor-not-allowed" : ""}`}
              disabled={isHidden} // Tombol akan benar-benar tidak bisa diklik
            >
              신청하기
            </button>
          </div>
        </Link>
      )}
      {/* Popup Modal Carousel */}
      {selectedImage && (
        <ImageViewer
          images={response.data.images ?? []}
          initialIndex={currentIndex}
          onClose={closeImagePopup}
        />
      )}
    </div>
  );
}

export default DetailPage;
